import * as THREE from "three";

/** Main-thread copy of the per-column surface heights (from region maps) for picking and draping plans. */
export class HeightField {
  private regions = new Map<string, Int16Array>();
  private colors = new Map<string, Uint8Array>();
  cutY: number | null = null;

  set(rx: number, rz: number, height: Int16Array, color: Uint8Array) {
    this.regions.set(`${rx},${rz}`, height);
    this.colors.set(`${rx},${rz}`, color);
  }

  clear() {
    this.regions.clear();
    this.colors.clear();
  }

  /** Top of the highest block in column (x,z) = y of the surface you stand on; null if unknown. */
  raw(x: number, z: number): number | null {
    x = Math.floor(x); z = Math.floor(z);
    const h = this.regions.get(`${x >> 9},${z >> 9}`);
    if (!h) return null;
    const i = (z & 511) * 512 + (x & 511);
    const c = this.colors.get(`${x >> 9},${z >> 9}`)!;
    if (c[i * 4 + 3] === 0) return null;
    return h[i];
  }

  at(x: number, z: number): number | null {
    const h = this.raw(x, z);
    if (h === null) return null;
    return this.cutY !== null ? Math.min(h, this.cutY + 1) : h;
  }

  atOr(x: number, z: number, fallback: number) {
    return this.at(x, z) ?? fallback;
  }

  /** Ray-march the height field. Returns the hit block column and surface y. */
  pick(ray: THREE.Ray, maxDist = 6000): { x: number; y: number; z: number } | null {
    const o = ray.origin, d = ray.direction;
    let t = 0;
    // start where the ray enters the plausible world height band
    const top = this.cutY !== null ? this.cutY + 1 : 330;
    if (o.y > top && d.y < 0) t = (o.y - top) / -d.y;
    const step = 0.25;
    for (; t < maxDist; t += step * (1 + t / 800)) {
      const px = o.x + d.x * t, py = o.y + d.y * t, pz = o.z + d.z * t;
      if (py < -70) break;
      const h = this.at(px, pz);
      if (h !== null && py <= h) {
        return { x: Math.floor(px), y: h, z: Math.floor(pz) };
      }
    }
    // fall back to sea-level plane so tools still work over unloaded areas
    if (d.y < 0) {
      const tt = (o.y - 63) / -d.y;
      return { x: Math.floor(o.x + d.x * tt), y: 63, z: Math.floor(o.z + d.z * tt) };
    }
    return null;
  }
}
